<template>
  <div
    ref="container"
    class="vidlist-container"
  >
    <VideoFilter
      :items="items"
      @input="val => (filteredItems = val)"
    >
      <template #default="v">
        <div
          v-show="filteredItems.length"
          class="vidlist_videos"
        >
          <div
            ref="videoCont"
            class="vidlist_video position-relative f-column"
            :class="{ 'ended-preview': vimeoVideoEnded }"
          >
            <div
              v-if="activeVideo.in_post_production"
              class="get-premium-overlay f-center flex-1 f-column anim-fade-in"
              style="background: linear-gradient(223deg, #151515, transparent)"
            >
              <div class="mdi mdi-movie-open clr-grey text-big"></div>
              <div class="mx-auto px-4 mt-2 text-center">
                <span
                  class="clr-grey"
                  v-html="$t('messages.coaching_video_in_post_production')"
                >
                </span>
              </div>
            </div>

            <div
              v-else-if="
                previewEnded ||
                (!activeVideo.in_post_production &&
                  !activeVideo.video_id &&
                  !activeVideo.preview_video_id)
              "
              class="get-premium-overlay f-center flex-1 f-column anim-fade-in_slow"
              style="background: linear-gradient(223deg, #151515, transparent); z-index: 4"
            >
              <div class="mx-auto pa-4 text-center">
                <span
                  class="clr-grey text-semi-large"
                  v-html="
                    previewEnded
                      ? $t('messages.coaching_upgrade_to_continue_watching_message', {
                          tier: $t('tier.options.premium'),
                          gametype: '',
                        })
                      : $t('messages.coaching_upgrade_message_tra01', {
                          tier: $t('tier.options.premium'),
                          gametype: '',
                        })
                  "
                >
                </span>
              </div>
              <v-btn
                :to="{ name: 'Subscription' }"
                color="primary"
                size="x-large"
                class="mx-auto"
                style="min-width: 12.5rem"
              >
                <v-icon class="mr-2">mdi-crown</v-icon>
                {{ $t('btn.upgrade_watch') }}
              </v-btn>
            </div>

            <div
              v-else-if="!activeVideo.video_id && activeVideo.preview_video_id"
              class="upgrade-bar anim-fade-in"
            >
              <div
                class="clr-grey my-auto px-4"
                v-html="
                  $t('messages.coaching_upgrade_to_continue_watching_message', {
                    tier: $t('tier.options.premium'),
                    gametype: '',
                  })
                "
              ></div>
              <v-btn
                :to="{ name: 'Subscription' }"
                color="primary"
                class="mr-autos"
                style="min-width: 12.5rem"
              >
                <v-icon class="mr-2">mdi-crown</v-icon>
                {{ $t('btn.upgrade') }}
              </v-btn>
            </div>

            <div
              v-if="activeVideo.source_id"
              class="position-relative d-flex flex-1 z-index_1 mw-0 mh-0"
              :style="`${
                vimeoVideoEnded && !activeVideo.video_id && activeVideo.preview_video_id
                  ? 'display:none;'
                  : ''
              }`"
            >
              <GwVideoPlayer
                class="d-flex flex-1"
                style="max-height: 100%; max-width: 100%"
                :type="activeVideo.provider"
                :src="activeVideo.source_id"
                @vimeo:play="setVideoPlay"
                @vimeo:ended="setVideoEnd"
              />
            </div>
          </div>

          <VirtualList
            ref="scroller"
            :items="filteredItems"
            :estimateSize="110"
            idKey="id"
            :keeps="20"
            :hasDynamicSize="true"
            class="vidlist_list small-scroll-track"
          >
            <template #before>
              <slot name="head"></slot>
            </template>
            <template #item="{ item, index }">
              <div
                :ref="`vid_${item.id}`"
                class="vidlist_list_item"
                :class="[
                  {
                    vidlist_list_item_active: activeVideo ? activeVideo.id === item.id : false,
                  },
                ]"
                @click="setActiveVideo(item)"
              >
                <div class="vidlist_list_item_text">
                  <div
                    class="my-auto f-center text-smallest"
                    style="
                      color: var(--clr-black-4);
                      position: absolute;
                      left: -1.5rem;
                      top: -0.5rem;
                      height: 1rem;
                      min-width: 1rem;
                    "
                  >
                    {{ v.sortOrder === 'desc' ? filteredItems.length - index : index + 1 }}
                  </div>
                  <div
                    class="text-normal-large"
                    :title="item.name"
                  >
                    {{ item.name }}
                  </div>
                  <div class="d-flex f-center">
                    <div
                      class="vidlist_tag"
                      :style="`--clr-tag:var(--clr-${item.format})`"
                    >
                      {{
                        item.format === 'theory'
                          ? $t(`common.theory`)
                          : $t(`gametypes.groups.${item.format}`)
                      }}
                    </div>
                    <div class="pl-2 clr-grey text-small mr-auto">
                      {{ item.datetime_locale }} -
                      {{ item.coach_name }}
                    </div>
                  </div>
                  <div
                    v-if="!item.description"
                    :title="item.description"
                    class="clr-grey text-small truncate2"
                  >
                    {{ item.description }}
                  </div>
                  <div
                    class="vidlist_list_item_togglers mr-auto"
                    :class="{
                      'no-events opacity_4': item.in_post_production,
                    }"
                    @click.stop
                  >
                    <Checkbox
                      class="csst csst_nm"
                      :value="item.is_favorite"
                      style="
                        --icon-size: var(--toggler-icon-size);
                        --clr-disabled: var(--clr-grey-2);
                        --clr-enabled: var(--clr-yellow);
                      "
                      :icons="['mdi-star-outline', 'mdi-star']"
                      @click="$emit('toggle:favorite', item)"
                    >
                      <template #content>
                        <div
                          class="csst_content clr-white text-nowrap"
                          style="position: absolute; left: 1.75rem"
                          v-html="$t('tooltips.coaching_video_toggle_favorite')"
                        />
                      </template>
                    </Checkbox>

                    <Checkbox
                      class="csst csst_nm"
                      :value="item.is_watched"
                      style="
                        --icon-size: var(--toggler-icon-size);
                        --clr-disabled: var(--clr-grey-2);
                        --clr-enabled: var(--clr-primary);
                      "
                      :icons="['mdi-eye-check-outline', 'mdi-eye-check']"
                      @click="$emit('toggle:watched', item)"
                    >
                      <template #content>
                        <div
                          class="csst_content clr-white text-nowrap"
                          style="position: absolute; left: 1.75rem"
                          v-html="$t('tooltips.coaching_video_toggle_watched')"
                        />
                      </template>
                    </Checkbox>
                  </div>
                </div>

                <div class="f-column f-center ml-auto">
                  <i
                    large
                    class="mdi text-ultra icon_btns"
                    :class="`${
                      item.in_post_production
                        ? 'mdi-movie-open'
                        : activeVideo.id === item.id
                          ? 'mdi-play-circle-outline'
                          : 'mdi-play-circle'
                    }`"
                    >{{
                  }}</i>
                </div>
              </div>
            </template>
          </VirtualList>
          <!-- <DynamicScroller
              ref="scroller"
              class="trahis_scroller no-scroll-trackx"
              :items="filteredItems"
              :direction="'vertical'"
              key-field="id"
              :min-item-size="mobile ? 80 : 110"
            >
              <template #default="{ item, index, active }">
                <DynamicScrollerItem
                  :item="item"
                  :active="active"
                  :data-index="index"
                >

                </DynamicScrollerItem>
              </template>
            </DynamicScroller> -->
          <!-- </div> -->
        </div>
        <div
          v-if="!filteredItems.length"
          class="vidlist_videos"
        >
          <div class="f-center f-column w-100 clr-grey weight_500 pa-16 text-center">
            <i class="mdi mdi-message text-big"></i>
            {{ $t('messages.no_videos_try_changing_filter_parameters') }}
          </div>
        </div>
      </template>
    </VideoFilter>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import Checkbox from '@/components/Common/Checkbox/Checkbox'
import VideoFilter from '@/components/lib/VideoFilter'
import VirtualList from '@/components/vue3/VirtualList/VirtualList'
import GwVideoPlayer from '@/components/lib/GwVideoPlayer'

export default {
  name: 'VideoList',
  components: {
    GwVideoPlayer,
    VideoFilter,
    VirtualList,
    Checkbox,
  },
  props: {
    items: { default: () => [] },
  },
  emits: ['set:watched', 'toggle:favorite', 'toggle:watched'],
  data() {
    return {
      filteredItems: [],
      activeVideo: {},
      vimeoVideoEnded: false,
    }
  },
  mounted() {
    nextTick(() => {
      this.setVideo()
      if (this.activeVideo) {
        this.scrollToVideo(this.activeVideo)
      }
    })
  },
  methods: {
    setActiveVideo(item) {
      this.video = item
      this.$router
        .replace({
          query: {
            ...this.$route.query,
            video_id: item.id,
            // vid_coaches: item.coach_id,
          },
        })
        .catch(() => {})
      this.scrollToVideo(item)
    },
    setVideoPlay() {
      console.log('setVideoPlay')
      this.vimeoVideoEnded = false
    },
    setVideoEnd() {
      console.log('setVideoEnd')
      this.vimeoVideoEnded = Date.now()
    },
    setVideo() {
      let res = this.filteredItems.find(item => item.id === this.activeVideoId)
      res = res || this.filteredItems[0] || {}
      this.activeVideo = res
      this.vimeoVideoEnded = false

      if (this.activeVideoId !== res.id) {
        this.setActiveVideo(res)
      }
    },
    scrollToVideo(video) {
      setTimeout(() => {
        const { scroller } = this.$refs
        if (scroller && video?.id) {
          const idx = this.filteredItems.findIndex(i => i.id === video.id)
          if (this.$refs.scroller) {
            this.$refs.scroller?.scrollToIndex(idx)
          }
        }
        setTimeout(() => {
          if (this.$refs.container) {
            this.$refs.container.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center',
            })
          }
        }, 220)
      }, 220)
    },
  },
  computed: {
    previewEnded() {
      return (
        this.vimeoVideoEnded &&
        this.activeVideo.provider === 'vimeo' &&
        !this.activeVideo?.video_id &&
        this.activeVideo?.preview_video_id
      )
    },
    activeVideoId() {
      const res = this.$route.query.video_id || ''
      return res
    },
    mobile() {
      const { globals_mobile } = this.$store.getters
      return globals_mobile
    },
  },
  watch: {
    activeVideoId: {
      immediate: false,
      handler: 'setVideo',
    },
    filteredItems: {
      immediate: false,
      handler: 'setVideo',
    },
  },
}
</script>
<style lang="css">
html {
  background: black !important;
}
.vp-center {
  background: black !important;
}
</style>
<style lang="scss" scoped>
.vidlist-container {
  flex: 1;
  // display: flex;
  width: 94rem;
  max-width: 100vw;
}
.upgrade-bar {
  display: flex;
  // align-items: flex-end;
  justify-content: flex-end;
  padding: 1rem;
  position: relative;
  background: var(--clr-black-1);
}

.vidlist {
  &_videos {
    margin-top: 1rem;
    overflow: hidden;
    border-radius: 8px;
    min-height: 42rem;
    max-height: 42rem;
    border: solid 2px var(--clr-black-3);
    display: flex;
    flex: 1;
    box-sizing: content-box;
  }
  &_video {
    // flex: 1;
    display: flex;
    flex-direction: column;
    background: var(--clr-black-0);
    background: black;
    min-height: 19rem;
    // max-width: 100vw;
    width: 60%;
    overflow: hidden;

    // max-width: 65%;
  }
  &_list {
    overflow-y: auto;
    overflow-x: hidden;
    // width: 32rem;
    flex: 1;
    max-width: 100vw;
    flex-shrink: 0;
    &_item {
      position: relative;
      min-height: 5rem;
      padding: 1.25rem 2rem;
      font-size: 1rem;
      font-weight: 500;
      display: flex;
      align-items: center;
      background: var(--clr-black-0);
      cursor: pointer;
      overflow: hidden;
      position: relative;
      &_togglers {
        // position: absolute;
        // top: -1rem;
        // left: -1.6rem;
        display: flex;
        // flex-direction: column;
        gap: 0.5rem;
        // padding: 0.25rem 0 0 0;
        --toggler-icon-size: 1.25rem;
        // top: 0.75rem;
        // right: 1.75rem;
        // opacity: ;
      }
      &_text {
        display: flex;
        // align-items: center;
        gap: 0.25rem;
        // justify-content: center;
        flex-direction: column;
        max-width: calc(100% - 4rem);
        position: relative;
      }
      &_tag {
        position: absolute;
        z-index: 1;
        bottom: 0.7rem;
        right: -2.1rem;
        font-size: 1rem;
        color: var(--clr-white-0) !important;
        width: 7rem;
        text-align: center;
        transform: rotate(-45deg);
        font-weight: 500;
      }
      &:before {
        content: '';
        position: absolute;
        height: 1px;
        width: 100%;
        left: 0;
        position: absolute;
        margin: 0 auto;
        bottom: 0;
        z-index: 1;
        background: var(--clr-black-3);
      }
      i {
        color: var(--clr-grey-2);
      }
      &:hover {
        background: var(--clr-black-1);
        color: var(--clr-primary);
        i {
          color: var(--clr-primary);
        }
      }
      &_active {
        background: var(--clr-black-1);

        color: var(--clr-primary);
        i {
          color: var(--clr-primary);
        }
      }
    }
  }
  &_tag {
    border-radius: 4px;
    background: var(--clr-black-2);
    font-size: 0.8rem;
    color: var(--clr-white-0);
    padding: 0 0.25rem;
    margin: 0.25rem 0.25rem 0.25rem 0;
    text-transform: uppercase;
    position: relative;
    white-space: nowrap;
    overflow: hidden;
    padding: 0 0.25rem 0 0.75rem;
    --clr-tag: red;
    &:before {
      content: '';
      height: 100%;
      width: 6px;
      position: absolute;
      background: var(--clr-tag);
      left: 0;
      top: 0;
    }
  }
}

@media (max-width: 1023px) {
  .vidlist {
    &_video {
      width: 100%;
      max-height: 24rem;
      min-height: 24rem;
    }
    &_videos {
      flex-direction: column;
      max-height: unset;
      margin-top: 0.75rem;
    }
    &_list {
      height: 34rem;
      max-height: calc(var(--vh-9) - 12rem);
      width: 100%;
      &_item {
        min-height: 80px;
        &_togglers {
          --toggler-icon-size: 1.5rem;
        }
      }
    }
  }
}

@media (max-width: 666px) {
  .upgrade-bar {
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 1rem;
  }
}
</style>
